import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Skeleton from '@mui/material/Skeleton';
import Typography from '@mui/material/Typography';
import { Link, useNavigate } from '@tanstack/react-router';
import type { ReactNode } from 'react';
import { useWorkflowCatalog } from '../../lib/workflows.hooks';
import type { Stage } from '../filters/StageToggle';
import { MediaCard } from '../media/shared/MediaCard';
import { WorkflowCard } from '../workflows/WorkflowCard';
import type { CatalogGroup, WorkflowCard as WorkflowCardData } from '../workflows/workflows.types';
import { SectionHeader } from './SectionHeader';

const SUGGESTION_LIMIT = 3;
const SKELETON_KEYS = ['first', 'second', 'third'];

const GRID_SX = {
  display: 'grid',
  gridTemplateColumns: {
    xs: '1fr',
    md: 'repeat(2, minmax(0, 1fr))',
    lg: 'repeat(3, minmax(0, 1fr))',
  },
  gap: 3,
};

interface StartWorkflowSectionProps {
  stage: Stage;
  accountIds: number[];
}

interface EmptySlotProps {
  shownCount: number;
}

interface Suggestion {
  card: WorkflowCardData;
  group: CatalogGroup;
}

function EmptySlot({ shownCount }: EmptySlotProps): ReactNode {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        p: 4,
        border: '1px dashed',
        borderColor: 'divider',
        borderRadius: 2,
      }}
    >
      <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
        {shownCount === 0
          ? 'No workflows for this scope'
          : `Only ${shownCount} ${shownCount === 1 ? 'workflow' : 'workflows'} for this scope`}
      </Typography>
      <Typography sx={{ color: 'text.secondary' }}>
        Switch stage in the top filter bar, or browse every workflow
      </Typography>
      <Link
        to="/workflows"
        search={(previous) => previous}
        style={{ color: 'inherit', fontWeight: 600 }}
      >
        Browse all workflows →
      </Link>
    </Box>
  );
}

function suggestions(groups: CatalogGroup[], stage: Stage): Suggestion[] {
  const seen = new Set<string>();
  const unique: Suggestion[] = [];
  for (const group of groups) {
    if (stage !== 'all' && group.stage !== stage) continue;
    for (const card of group.items) {
      if (seen.has(card.workflowId)) continue;
      seen.add(card.workflowId);
      unique.push({ card, group });
    }
  }
  return unique
    .sort((left, right) => right.card.runCount - left.card.runCount)
    .slice(0, SUGGESTION_LIMIT);
}

export function StartWorkflowSection({ stage, accountIds }: StartWorkflowSectionProps): ReactNode {
  const navigate = useNavigate();
  const catalog = useWorkflowCatalog(accountIds);
  const shown = suggestions(catalog.data?.groups ?? [], stage);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <SectionHeader
        title="Start a workflow"
        subtitle="Recommended for your current scope"
        linkLabel="Browse all workflows →"
        onLinkClick={() => void navigate({ to: '/workflows', search: (previous) => previous })}
      />
      {catalog.isError ? (
        <Alert severity="error">Workflows could not be loaded.</Alert>
      ) : catalog.isPending ? (
        <Box sx={GRID_SX}>
          {SKELETON_KEYS.map((key) => (
            <MediaCard key={key} sx={{ p: 3 }}>
              <Skeleton variant="text" width={160} />
              <Skeleton variant="rounded" height={72} sx={{ mt: 2 }} />
            </MediaCard>
          ))}
        </Box>
      ) : (
        <Box sx={GRID_SX}>
          {shown.map(({ card, group }) => (
            <WorkflowCard
              key={card.workflowId}
              card={card}
              stage={group.stage}
              stageLabel={group.label}
              footer={`${stage === 'all' ? 'All stages' : group.label} · ${card.runCount} ${card.runCount === 1 ? 'run' : 'runs'}`}
              onOpen={(workflowId, goal) =>
                void navigate({
                  to: '/workflows/$workflowId',
                  params: { workflowId },
                  search: (previous) => ({ ...previous, goal }),
                })
              }
            />
          ))}
          {shown.length < SUGGESTION_LIMIT ? <EmptySlot shownCount={shown.length} /> : null}
        </Box>
      )}
    </Box>
  );
}
